import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../services/supabase';
import { ThemeToggle } from './ThemeToggle';
import BrandMark from './ui/BrandMark';
import { ChevronDown, LogIn, Menu, UserPlus, X } from 'lucide-react';

interface HeaderProps {
  openSignIn: () => void;
  openSignUp: () => void;
}

const TOOL_LINKS = [
  { to: '/financial-calculator', label: 'Financial Impact', badge: 'Free' },
  { to: '/custody-analysis', label: 'Custody Analysis', badge: 'Free' },
  { to: '/child-support-calculator', label: 'Child Support', badge: 'Free' },
  { to: '/actual-time', label: 'Actual Time Tracker', badge: 'Standard' },
  { to: '/scenario-compare', label: 'Scenario Compare', badge: 'Premium' },
  { to: '/court-packet', label: 'Court Packet', badge: 'Premium' },
  { to: '/attorney-share', label: 'Attorney Share', badge: 'Premium' },
  { to: '/evidence-report', label: 'Evidence Report', badge: 'Premium' },
];

const Header: React.FC<HeaderProps> = ({ openSignIn, openSignUp }) => {
  const { user, loading } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const toolsDropdownRef = useRef<HTMLDivElement>(null);
  const toolsButtonRef = useRef<HTMLButtonElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;
  const isToolsActive = TOOL_LINKS.some((link) =>
    link.to === '/child-support-calculator'
      ? location.pathname.startsWith('/child-support-calculator')
      : location.pathname === link.to
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownOpen &&
        dropdownRef.current &&
        buttonRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }

      if (
        toolsOpen &&
        toolsDropdownRef.current &&
        toolsButtonRef.current &&
        !toolsDropdownRef.current.contains(event.target as Node) &&
        !toolsButtonRef.current.contains(event.target as Node)
      ) {
        setToolsOpen(false);
      }

      if (
        mobileMenuOpen &&
        mobileMenuRef.current &&
        mobileMenuButtonRef.current &&
        !mobileMenuRef.current.contains(event.target as Node) &&
        !mobileMenuButtonRef.current.contains(event.target as Node)
      ) {
        setMobileMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && dropdownOpen) {
        setDropdownOpen(false);
        buttonRef.current?.focus();
      }

      if (event.key === 'Escape' && toolsOpen) {
        setToolsOpen(false);
        toolsButtonRef.current?.focus();
      }

      if (event.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
        mobileMenuButtonRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [dropdownOpen, toolsOpen, mobileMenuOpen]);

  useEffect(() => {
    setDropdownOpen(false);
    setToolsOpen(false);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinkClass = (path: string) =>
    `text-sm px-3.5 py-2 rounded-full transition-colors relative min-h-9 inline-flex items-center ${
      isActive(path)
        ? 'font-semibold text-primary bg-primary/[0.09] dark:bg-primary/[0.12]'
        : 'font-medium text-muted-foreground hover:text-foreground'
    }`;

  const mobileNavLinkClass = (path: string) =>
    `flex min-h-11 items-center rounded-full px-4 text-sm transition-colors ${
      isActive(path)
        ? 'font-semibold bg-primary/[0.09] dark:bg-primary/[0.12] text-primary'
        : 'font-medium text-muted-foreground hover:text-foreground hover:bg-secondary'
    }`;

  const handleMobileSignUp = () => {
    setMobileMenuOpen(false);
    openSignUp();
  };

  const handleMobileSignIn = () => {
    setMobileMenuOpen(false);
    openSignIn();
  };

  const handleSignOut = () => {
    supabase.auth.signOut();
    setDropdownOpen(false);
    setToolsOpen(false);
    setMobileMenuOpen(false);
  };

  const renderToolsMenu = () => (
    <div className="relative">
      <button
        ref={toolsButtonRef}
        onClick={() => setToolsOpen(!toolsOpen)}
        className={`text-sm px-3.5 py-2 rounded-full transition-colors flex min-h-9 items-center gap-1 ${
          isToolsActive
            ? 'font-semibold text-primary bg-primary/[0.09] dark:bg-primary/[0.12]'
            : 'font-medium text-muted-foreground hover:text-foreground'
        }`}
        aria-expanded={toolsOpen}
        aria-haspopup="true"
      >
        Tools
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${toolsOpen ? 'rotate-180' : ''}`} />
      </button>
      <div
        ref={toolsDropdownRef}
        className={`absolute left-0 mt-2 w-72 rounded-2xl border border-border bg-popover shadow-lift z-10 overflow-hidden p-1.5 transition-all duration-200 origin-top-left ${
          toolsOpen
            ? 'opacity-100 scale-100 translate-y-0'
            : 'opacity-0 scale-95 -translate-y-1 pointer-events-none'
        }`}
        role="menu"
      >
        {TOOL_LINKS.map((link) => (
          <Link
            key={link.to}
            to={link.to}
            onClick={() => setToolsOpen(false)}
            className="flex items-center justify-between gap-3 rounded-xl px-3.5 py-2.5 text-sm hover:bg-primary/[0.06] dark:hover:bg-primary/[0.1] transition-colors"
            role="menuitem"
          >
            <span className="font-medium">{link.label}</span>
            <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
              {link.badge}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );

  const renderMobileToolLinks = () => (
    <>
      <div className="my-2 border-t" />
      <div className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Tools
      </div>
      {TOOL_LINKS.map((link) => (
        <Link
          key={link.to}
          to={link.to}
          className="flex min-h-11 items-center justify-between rounded-full px-4 text-sm font-medium transition-colors hover:bg-primary/[0.06] dark:hover:bg-primary/[0.1]"
          role="menuitem"
        >
          <span>{link.label}</span>
          <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
            {link.badge}
          </span>
        </Link>
      ))}
    </>
  );

  return (
    <header className="bg-background/70 backdrop-blur-xl px-4 border-b border-border sticky top-0 z-40 sm:px-6 lg:px-10">
      <div className="max-w-[1160px] mx-auto flex h-16 items-center justify-between gap-3">
        <div className="flex min-w-0 items-center">
          <Link to="/" className="flex min-w-0 items-center gap-[11px]">
            <BrandMark size={34} className="shrink-0 rounded-[9px] shadow-primary-glow" />
            <span className="truncate font-display text-[19px] font-semibold tracking-[-0.01em]">
              Custody Planner
            </span>
          </Link>
        </div>
        <nav className="hidden items-center gap-1 sm:flex sm:gap-2" aria-label="Main navigation">
          {loading ? (
            <div className="flex items-center gap-2">
              <div className="h-8 w-20 rounded-lg bg-secondary animate-pulse" />
              <div className="h-8 w-16 rounded-lg bg-primary/30 animate-pulse" />
            </div>
          ) : user ? (
            <>
              <Link to="/app" className={navLinkClass('/app')} aria-current={isActive('/app') ? 'page' : undefined}>
                Calendar
              </Link>
              {renderToolsMenu()}
              <Link to="/pricing" className={navLinkClass('/pricing')} aria-current={isActive('/pricing') ? 'page' : undefined}>
                Pricing
              </Link>
              <div className="relative">
                <button
                  ref={buttonRef}
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className={`text-sm px-3.5 py-2 rounded-full transition-colors flex min-h-9 items-center gap-1 ${
                    isActive('/profile')
                      ? 'font-semibold text-primary bg-primary/[0.09] dark:bg-primary/[0.12]'
                      : 'font-medium text-muted-foreground hover:text-foreground'
                  }`}
                  aria-expanded={dropdownOpen}
                  aria-haspopup="true"
                >
                  Account
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                <div
                  ref={dropdownRef}
                  className={`absolute right-0 mt-2 w-48 bg-popover rounded-2xl border border-border shadow-lift z-10 overflow-hidden p-1.5 transition-all duration-200 origin-top-right ${
                    dropdownOpen
                      ? 'opacity-100 scale-100 translate-y-0'
                      : 'opacity-0 scale-95 -translate-y-1 pointer-events-none'
                  }`}
                  role="menu"
                >
                  <Link
                    to="/profile"
                    onClick={() => setDropdownOpen(false)}
                    className="block rounded-xl px-3.5 py-2.5 text-sm font-medium hover:bg-primary/[0.06] dark:hover:bg-primary/[0.1] transition-colors"
                    role="menuitem"
                  >
                    Profile & Billing
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className="w-full text-left rounded-xl px-3.5 py-2.5 text-sm font-medium hover:bg-primary/[0.06] dark:hover:bg-primary/[0.1] transition-colors text-destructive"
                    role="menuitem"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              {renderToolsMenu()}
              <Link to="/pricing" className={navLinkClass('/pricing')} aria-current={isActive('/pricing') ? 'page' : undefined}>
                Pricing
              </Link>
              <button
                onClick={openSignIn}
                className="inline-flex min-h-9 items-center rounded-full border border-border bg-card px-4 py-2 text-sm font-medium transition-colors duration-150 hover:border-primary active:scale-[0.98]"
              >
                Sign in
              </button>
              <button
                onClick={openSignUp}
                className="inline-flex min-h-9 items-center rounded-full bg-primary px-[18px] py-2 text-sm font-semibold text-primary-foreground shadow-primary-glow transition-all duration-150 hover:brightness-[1.08] active:scale-[0.98]"
              >
                Start free
              </button>
            </>
          )}
          <ThemeToggle />
        </nav>

        <div className="relative flex shrink-0 items-center gap-1 sm:hidden">
          <ThemeToggle />
          <button
            ref={mobileMenuButtonRef}
            type="button"
            onClick={() => setMobileMenuOpen(prev => !prev)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-border bg-card text-muted-foreground transition-colors hover:text-foreground"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
            aria-haspopup="menu"
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>

          <div
            ref={mobileMenuRef}
            className={`absolute right-0 top-full mt-2 w-[min(calc(100vw-2rem),18rem)] rounded-2xl border border-border bg-popover p-2 shadow-lift transition-all duration-200 origin-top-right ${
              mobileMenuOpen
                ? 'opacity-100 scale-100 translate-y-0'
                : 'pointer-events-none opacity-0 scale-95 -translate-y-1'
            }`}
            role="menu"
          >
            {loading ? (
              <div className="space-y-2 p-1">
                <div className="h-11 rounded-lg bg-secondary animate-pulse" />
                <div className="h-11 rounded-lg bg-secondary animate-pulse" />
              </div>
            ) : user ? (
              <>
                <Link to="/app" className={mobileNavLinkClass('/app')} role="menuitem">
                  Calendar
                </Link>
                <Link to="/pricing" className={mobileNavLinkClass('/pricing')} role="menuitem">
                  Pricing
                </Link>
                {renderMobileToolLinks()}
                <Link to="/profile" className={mobileNavLinkClass('/profile')} role="menuitem">
                  Profile &amp; Billing
                </Link>
                <div className="my-2 border-t" />
                <button
                  onClick={handleSignOut}
                  className="flex min-h-11 w-full items-center rounded-lg px-3 text-left text-sm font-medium text-destructive transition-colors hover:bg-secondary"
                  role="menuitem"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link to="/pricing" className={mobileNavLinkClass('/pricing')} role="menuitem">
                  Pricing
                </Link>
                {renderMobileToolLinks()}
                <button
                  onClick={handleMobileSignIn}
                  className="flex min-h-11 w-full items-center gap-2 rounded-full px-4 text-left text-sm font-medium transition-colors hover:bg-primary/[0.06] dark:hover:bg-primary/[0.1]"
                  role="menuitem"
                >
                  <LogIn className="h-4 w-4" />
                  Sign in
                </button>
                <button
                  onClick={handleMobileSignUp}
                  className="mt-1 flex min-h-11 w-full items-center justify-center gap-2 rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-primary-glow transition-all hover:brightness-[1.08]"
                  role="menuitem"
                >
                  <UserPlus className="h-4 w-4" />
                  Start free
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
